import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Alert, Button } from 'react-bootstrap';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useGuardedLogout } from '../../hooks/useGuardedLogout';

const ChangePasswordRequired: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const { requestLogout, confirm: signOutConfirm } = useGuardedLogout();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword.length < 8) {
      setError(t('auth.changePassword.errors.tooShort'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('auth.changePassword.errors.mismatch'));
      return;
    }

    setSubmitting(true);
    try {
      await axios.patch('/api/v1/users/me/password', { newPassword }, { withCredentials: true });
      // Refresh the cached user so mustChangePassword=false propagates and the gate releases.
      if (typeof refreshUser === 'function') await refreshUser();
      navigate('/', { replace: true });
    } catch (err: any) {
      setError(err?.response?.data?.error || t('auth.changePassword.errors.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container className="mt-5" data-testid="change-password-required">
      <Row className="justify-content-md-center">
        <Col xs={12} md={6} lg={4}>
          <Card className="shadow-sm">
            <Card.Body className="p-4">
              <div className="text-center mb-4">
                <h2 className="fw-bold">{t('auth.changePassword.title')}</h2>
                <p className="text-muted">{t('auth.changePassword.subtitle')}</p>
              </div>

              {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3" controlId="newPassword">
                  <Form.Label className="small fw-bold text-secondary">
                    {t('auth.changePassword.newPasswordLabel')}
                  </Form.Label>
                  <Form.Control
                    type="password"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-4" controlId="confirmPassword">
                  <Form.Label className="small fw-bold text-secondary">
                    {t('auth.changePassword.confirmPasswordLabel')}
                  </Form.Label>
                  <Form.Control
                    type="password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                </Form.Group>

                <Button
                  type="submit"
                  variant="primary"
                  className="w-100 py-2 fw-bold"
                  disabled={submitting}
                >
                  {t('auth.changePassword.submit')}
                </Button>
              </Form>

              <div className="text-center mt-3">
                <Button
                  type="button"
                  variant="link"
                  className="small text-secondary"
                  onClick={() => { requestLogout(); }}
                >
                  {t('auth.changePassword.logout')}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      {signOutConfirm}
    </Container>
  );
};

export default ChangePasswordRequired;
